import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  FlatList,
  Alert,
  RefreshControl,
  ActivityIndicator,
  Modal,
  TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { 
  getCaremRawData, 
  getUberRawData, 
  getSalikRawData, 
  getFinesRawData,
  getAccountingViewSummary,
  getDrivers,
  getVehicles,
  getDriverBalances,
  getAccountingSummary
} from '../api/supabase';
import { getDetailedDriverBreakdown } from '../utils/detailedDriverBreakdown';

const AccountingViewScreen = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [overview, setOverview] = useState(null);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    processed: null, // null = all, true = processed, false = unprocessed
    driverId: '',
    vehicleId: ''
  });
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  
  // Modal state for driver details
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  
  // Breakdown toggle states
  const [showRentBreakdown, setShowRentBreakdown] = useState(false);
  const [showSalikBreakdown, setShowSalikBreakdown] = useState(false);
  const [showFinesBreakdown, setShowFinesBreakdown] = useState(false);
  const [showIncomeBreakdown, setShowIncomeBreakdown] = useState(false);

  // Helper functions for formatting
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'AED 0.00';
    return `AED ${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabs = [
    { key: 'overview', title: 'Overview', color: '#6366F1', icon: 'analytics' },
    { key: 'balances', title: 'Driver Balances', color: '#F59E0B', icon: 'wallet' },
    { key: 'careem', title: 'Careem', color: '#00D4AA', icon: 'car' },
    { key: 'uber', title: 'Uber', color: '#000000', icon: 'car-sport' },
    { key: 'salik', title: 'Salik', color: '#2196F3', icon: 'radio' },
    { key: 'fines', title: 'Fines', color: '#F44336', icon: 'warning' },
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadTabData();
  }, [activeTab, filters]);

  const loadInitialData = async () => {
    try {
      const [driversData, vehiclesData] = await Promise.all([
        getDrivers(),
        getVehicles()
      ]);
      
      setDrivers(driversData);
      setVehicles(vehiclesData);
      
      loadSummary();
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadSummary = async () => {
    try {
      const result = await getAccountingViewSummary(filters);
      if (result.data) {
        setSummary(result.data);
      }
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const loadTabData = async () => {
    setLoading(true);
    try {
      let result;
      switch (activeTab) {
        case 'overview':
          result = await getAccountingSummary(filters.dateFrom || filters.dateTo ? {
            startDate: filters.dateFrom,
            endDate: filters.dateTo
          } : undefined);
          if (result.data) {
            setOverview(result.data);
          }
          return;
        
        case 'balances':
          result = await getDriverBalances();
          // getDriverBalances returns array directly, not { data: [...] }
          if (result) {
            // Load detailed breakdown for each driver to get accurate calculations
            const driversWithBreakdown = await Promise.all(
              result.map(async (driver) => {
                try {
                  const detailedBreakdown = await getDetailedDriverBreakdown(driver.driverId);
                  return { ...driver, detailedBreakdown };
                } catch (error) {
                  console.error(`Error loading breakdown for driver ${driver.driverId}:`, error);
                  return driver; // Return without breakdown if error
                }
              })
            );
            setBalances(driversWithBreakdown);
          }
          return;
        
        case 'careem':
          result = await getCaremRawData(filters);
          break;
        case 'uber':
          result = await getUberRawData(filters);
          break;
        case 'salik':
          result = await getSalikRawData(filters);
          break;
        case 'fines':
          result = await getFinesRawData(filters);
          break;
        default:
          result = { data: [] };
      }

      if (result.data) {
        setData(result.data);
      } else if (result.error) {
        Alert.alert('Error', result.error);
      }
    } catch (error) {
      console.error('Error loading tab data:', error);
      Alert.alert('Error', 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadTabData(), loadSummary()]);
    setRefreshing(false);
  };

  const handlePayment = (driver: any) => {
    const balance = driver.currentBalance || driver.current_balance || 0;
    const driverName = driver.driver?.name || driver.driver_name || `Driver ${driver.driverId || driver.driver_id}`;
    
    if (balance >= 0) {
      // Positive balance - company owes driver
      Alert.alert(
        'Pay Driver',
        `Record payment of ${formatCurrency(balance)} to ${driverName}?`,
        [
          { text: 'Cancel', style: 'cancel' },
          { 
            text: 'Record Payment', 
            onPress: () => recordPayment(driver.driverId || driver.driver_id, balance, 'payment_to_driver')
          }
        ]
      );
    } else {
      // Negative balance - driver owes company
      Alert.alert(
        'Collect Payment',
        `Record payment of ${formatCurrency(Math.abs(balance))} from ${driverName}?`,
        [
          { text: 'Cancel', style: 'cancel' },
          { 
            text: 'Record Payment', 
            onPress: () => recordPayment(driver.driverId || driver.driver_id, Math.abs(balance), 'payment_from_driver')
          }
        ]
      );
    }
  };

  const recordPayment = async (driverId: string, amount: number, type: string) => {
    try {
      // Here you would implement the actual payment recording logic
      // For now, just show success and refresh data
      Alert.alert('Success', `Payment of ${formatCurrency(amount)} recorded successfully!`);
      await onRefresh();
      setModalVisible(false);
    } catch (error) {
      console.error('Error recording payment:', error);
      Alert.alert('Error', 'Failed to record payment');
    }
  };

  const renderOverviewItem = () => {
    if (!overview) return null;
    
    return (
      <View style={styles.overviewContainer}>
        <Text style={styles.overviewTitle}>Financial Overview</Text>
        
        <View style={styles.overviewGrid}>
          <View style={styles.overviewCard}>
            <Text style={styles.overviewLabel}>Total Income</Text>
            <Text style={styles.overviewValue}>{formatCurrency(overview.totalIncome || 0)}</Text>
          </View>
          
          <View style={styles.overviewCard}>
            <Text style={styles.overviewLabel}>Total Expenses</Text>
            <Text style={styles.overviewValue}>{formatCurrency(overview.totalExpenses || 0)}</Text>
          </View>
          
          <View style={styles.overviewCard}>
            <Text style={styles.overviewLabel}>Net Profit</Text>
            <Text style={[styles.overviewValue, { color: overview.netProfit >= 0 ? '#10B981' : '#EF4444' }]}>
              {formatCurrency(overview.netProfit || 0)}
            </Text>
          </View>
          
          <View style={styles.overviewCard}>
            <Text style={styles.overviewLabel}>Outstanding Balance</Text>
            <Text style={styles.overviewValue}>{formatCurrency(overview.outstandingBalance || 0)}</Text>
          </View>
        </View>

        <View style={styles.categorySummary}>
          <Text style={styles.categoryTitle}>Income Breakdown</Text>
          {overview.incomeByCategory && Object.entries(overview.incomeByCategory).map(([category, amount]) => (
            <View key={category} style={styles.categoryRow}>
              <Text style={styles.categoryLabel}>{category}</Text>
              <Text style={styles.categoryAmount}>{formatCurrency(amount)}</Text>
            </View>
          ))}
        </View>

        <View style={styles.categorySummary}>
          <Text style={styles.categoryTitle}>Expense Breakdown</Text>
          {overview.expensesByCategory && Object.entries(overview.expensesByCategory).map(([category, amount]) => (
            <View key={category} style={styles.categoryRow}>
              <Text style={styles.categoryLabel}>{category}</Text>
              <Text style={styles.categoryAmount}>{formatCurrency(amount)}</Text>
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderBalanceItem = ({ item, index }) => (
    <TouchableOpacity
      style={[
        styles.tableRow,
        index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd
      ]}
      onPress={async () => {
        setSelectedDriver(item);
        setModalVisible(true);
        setLoadingBreakdown(true);
        
        // Reset breakdown toggles
        setShowRentBreakdown(false);
        setShowSalikBreakdown(false);
        setShowFinesBreakdown(false);
        setShowIncomeBreakdown(false);
        
        try {
          console.log('🔍 Raw driver data:', JSON.stringify(item, null, 2));
          console.log('🔍 Loading detailed breakdown for driver:', item.driverId || item.driver_id);
          const detailedBreakdown = await getDetailedDriverBreakdown(item.driverId || item.driver_id);
          
          const updatedDriver = {
            ...item,
            detailedBreakdown
          };
          
          console.log('📊 Detailed breakdown loaded:', {
            rentItems: detailedBreakdown.rent.details.length,
            salikItems: detailedBreakdown.salik.details.length,
            fineItems: detailedBreakdown.fines.details.length,
            incomeItems: detailedBreakdown.income.details.length
          });
          
          console.log('📊 Updated driver object:', JSON.stringify(updatedDriver, null, 2));
          
          setSelectedDriver(updatedDriver);
        } catch (error) {
          console.error('❌ Error loading detailed breakdown:', error);
          // Keep the basic driver data even if breakdown fails
        } finally {
          setLoadingBreakdown(false);
        }
      }}
      activeOpacity={0.7}
    >
      <Text style={[styles.tableCell, styles.driverNameColumn]} numberOfLines={2}>
        {item.driver?.name || item.driver_name || `Driver ${item.driverId || item.driver_id}`}
      </Text>
      <Text style={[
        styles.tableCell, 
        styles.balanceColumn,
        { color: (item.detailedBreakdown ? 
          ((item.detailedBreakdown.income?.total || 0) - 
           ((item.detailedBreakdown.rent?.total || 0) + 
            (item.detailedBreakdown.salik?.total || 0) + 
            (item.detailedBreakdown.fines?.total || 0) + 
            (item.detailedBreakdown.other?.total || 0))) : 
          (item.currentBalance || item.current_balance || 0)) >= 0 ? '#10B981' : '#EF4444' }
      ]}>
        {formatCurrency(item.detailedBreakdown ? 
          ((item.detailedBreakdown.income?.total || 0) - 
           ((item.detailedBreakdown.rent?.total || 0) + 
            (item.detailedBreakdown.salik?.total || 0) + 
            (item.detailedBreakdown.fines?.total || 0) + 
            (item.detailedBreakdown.other?.total || 0))) : 
          (item.currentBalance || item.current_balance || 0))}
      </Text>
      <Text style={[styles.tableCell, styles.incomeColumn, { color: '#10B981' }]}>
        {formatCurrency(item.detailedBreakdown?.income?.total || item.totalEarned || (item.total_careem_income || 0) + (item.total_uber_income || 0) || 0)}
      </Text>
      <Text style={[styles.tableCell, styles.rentColumn, { color: '#F59E0B' }]}>
        {formatCurrency(item.detailedBreakdown?.rent?.total || item.totalRentCharged || item.total_rent_charged || 0)}
      </Text>
      <Text style={[styles.tableCell, styles.finesColumn, { color: '#EF4444' }]}>
        {formatCurrency(item.detailedBreakdown?.fines?.total || item.totalFines || item.total_fines || 0)}
      </Text>
      <Text style={[styles.tableCell, styles.totalOwedColumn]}>
        {formatCurrency(item.detailedBreakdown ? 
          ((item.detailedBreakdown.rent?.total || 0) + 
           (item.detailedBreakdown.salik?.total || 0) + 
           (item.detailedBreakdown.fines?.total || 0) + 
           (item.detailedBreakdown.other?.total || 0)) :
          ((item.totalRentCharged || item.total_rent_charged || 0) +
           (item.totalFines || item.total_fines || 0) +
           (item.totalSalik || item.total_salik || 0) +
           (item.totalPaymentsReceived || item.total_payments_received || 0))
        )}
      </Text>
    </TouchableOpacity>
  );

  const renderCaremItem = ({ item, index }) => (
    <View style={[
      styles.tableRow,
      index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd
    ]}>
      <Text style={[styles.tableCell, styles.driverNameColumn]} numberOfLines={2}>
        {item.driver_name || 'Unknown Driver'}
      </Text>
      <Text style={[styles.tableCell, styles.captainIdColumn]}>
        {item.careem_captain_id || 'N/A'}
      </Text>
      <Text style={[styles.tableCell, styles.phoneColumn]} numberOfLines={1}>
        {item.phone_number || 'N/A'}
      </Text>
      <Text style={[styles.tableCell, styles.earningsColumn, { color: '#10B981' }]}>
        {formatCurrency(item.total_earnings)}
      </Text>
      <Text style={[styles.tableCell, styles.cashBalanceColumn, { color: '#8B5CF6' }]}>
        {formatCurrency(item.cash_balance)}
      </Text>
      <View style={[styles.tableCell, styles.statusColumn]}>
        <View style={[styles.miniStatusBadge, { 
          backgroundColor: item.processed_to_accounting ? '#4CAF50' : '#FF9800' 
        }]}>
          <Text style={styles.miniStatusText}>
            {item.processed_to_accounting ? 'Done' : 'Pending'}
          </Text>
        </View>
      </View>
    </View>
  );

  const renderUberItem = ({ item, index }) => (
    <View style={[
      styles.tableRow,
      index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd
    ]}>
      <Text style={[styles.tableCell, styles.driverNameColumn]} numberOfLines={2}>
        {item.full_name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || 'Unknown Driver'}
      </Text>
      <Text style={[styles.tableCell, styles.uberUuidColumn]} numberOfLines={1}>
        {item.uber_driver_uuid ? item.uber_driver_uuid.substring(0, 8) + '...' : 'N/A'}
      </Text>
      <Text style={[styles.tableCell, styles.earningsColumn, { color: '#10B981' }]}>
        {formatCurrency(item.total_income)}
      </Text>
      <Text style={[styles.tableCell, styles.netFareColumn, { color: '#3B82F6' }]}>
        {formatCurrency(item.net_fare)}
      </Text>
      <View style={[styles.tableCell, styles.statusColumn]}>
        <View style={[styles.miniStatusBadge, { 
          backgroundColor: item.processed_to_accounting ? '#4CAF50' : '#FF9800' 
        }]}>
          <Text style={styles.miniStatusText}>
            {item.processed_to_accounting ? 'Done' : 'Pending'}
          </Text>
        </View>
      </View>
    </View>
  );

  const renderSalikItem = ({ item, index }) => (
    <View style={[
      styles.tableRow,
      index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd
    ]}>
      <Text style={[styles.tableCell, styles.licensePlateColumn]} numberOfLines={1}>
        {item.license_plate || 'Unknown Plate'}
      </Text>
      <Text style={[styles.tableCell, styles.dateColumn]}>
        {formatDate(item.transaction_date)}
      </Text>
      <Text style={[styles.tableCell, styles.tollGateColumn]} numberOfLines={2}>
        {item.toll_gate_name || 'N/A'}
      </Text>
      <Text style={[styles.tableCell, styles.amountColumn, { color: '#EF4444' }]}>
        {formatCurrency(item.amount)}
      </Text>
      <Text style={[styles.tableCell, styles.driverNameColumn]} numberOfLines={1}>
        {item.drivers?.name || 'N/A'}
      </Text>
      <View style={[styles.tableCell, styles.statusColumn]}>
        <View style={[styles.miniStatusBadge, { 
          backgroundColor: item.processed_to_accounting ? '#4CAF50' : '#FF9800' 
        }]}>
          <Text style={styles.miniStatusText}>
            {item.processed_to_accounting ? 'Done' : 'Pending'}
          </Text>
        </View>
      </View>
    </View>
  );

  const renderFinesItem = ({ item, index }) => (
    <View style={[
      styles.tableRow,
      index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd
    ]}>
      <Text style={[styles.tableCell, styles.fineNumberColumn]} numberOfLines={1}>
        {item.fine_number || 'Unknown Fine'}
      </Text>
      <Text style={[styles.tableCell, styles.dateColumn]}>
        {formatDate(item.issue_date)}
      </Text>
      <Text style={[styles.tableCell, styles.licensePlateColumn]}>
        {item.license_plate || 'N/A'}
      </Text>
      <Text style={[styles.tableCell, styles.totalAmountColumn, { color: '#EF4444' }]}>
        {formatCurrency(item.total_amount)}
      </Text>
      <Text style={[styles.tableCell, styles.discountAmountColumn, { color: '#F59E0B' }]}>
        {formatCurrency(item.amount_after_discount)}
      </Text>
      <Text style={[styles.tableCell, styles.driverNameColumn]} numberOfLines={1}>
        {item.drivers?.name || 'N/A'}
      </Text>
      <View style={[styles.tableCell, styles.statusColumn]}>
        <View style={[styles.miniStatusBadge, { 
          backgroundColor: item.processed_to_accounting ? '#4CAF50' : '#FF9800' 
        }]}>
          <Text style={styles.miniStatusText}>
            {item.processed_to_accounting ? 'Done' : 'Pending'}
          </Text>
        </View>
      </View>
    </View>
  );

  // Helper function to get current data based on active tab
  const getCurrentData = () => {
    switch (activeTab) {
      case 'balances':
        return balances || [];
      case 'careem':
      case 'uber':
      case 'salik':
      case 'fines':
        return data || [];
      default:
        return [];
    }
  };

  const renderItem = ({ item, index }) => {
    switch (activeTab) {
      case 'balances':
        return renderBalanceItem({ item, index });
      case 'careem':
        return renderCaremItem({ item, index });
      case 'uber':
        return renderUberItem({ item, index });
      case 'salik':
        return renderSalikItem({ item, index });
      case 'fines':
        return renderFinesItem({ item, index });
      default:
        return null;
    }
  };

  const renderSummaryCard = () => {
    if (!summary) return null;

    return (
      <View style={styles.summaryContainer}>
        <Text style={styles.summaryTitle}>Data Summary</Text>
        <View style={styles.summaryGrid}>
          {tabs.map(tab => {
            const tabSummary = summary[tab.key];
            if (!tabSummary) return null;

            return (
              <View key={tab.key} style={[styles.summaryCard, { borderLeftColor: tab.color }]}>
                <View style={styles.summaryHeader}>
                  <Ionicons name={tab.icon} size={20} color={tab.color} />
                  <Text style={styles.summaryLabel}>{tab.title}</Text>
                </View>
                <Text style={styles.summaryCount}>
                  {tabSummary.total} records
                </Text>
                <Text style={styles.summaryProcessed}>
                  {tabSummary.processed} processed
                </Text>
                <Text style={styles.summaryAmount}>
                  {formatCurrency(
                    tabSummary.totalEarnings || 
                    tabSummary.totalIncome || 
                    tabSummary.totalAmount || 0
                  )}
                </Text>
              </View>
            );
          })}
        </View>
      </View>
    );
  };

  const renderFilters = () => (
    <View style={styles.filtersContainer}>
      <Text style={styles.filtersTitle}>Filters</Text>
      <View style={styles.filtersRow}>
        <TextInput
          style={[styles.filterInput, { flex: 1 }]}
          placeholder="From Date (YYYY-MM-DD)"
          value={filters.dateFrom}
          onChangeText={(text) => setFilters(prev => ({ ...prev, dateFrom: text }))}
        />
        <TextInput
          style={[styles.filterInput, { flex: 1, marginLeft: 10 }]}
          placeholder="To Date (YYYY-MM-DD)"
          value={filters.dateTo}
          onChangeText={(text) => setFilters(prev => ({ ...prev, dateTo: text }))}
        />
      </View>
      <View style={styles.filtersRow}>
        <TouchableOpacity
          style={[styles.filterButton, filters.processed === null && styles.filterButtonActive]}
          onPress={() => setFilters(prev => ({ ...prev, processed: null }))}
        >
          <Text style={[styles.filterButtonText, filters.processed === null && styles.filterButtonTextActive]}>
            All
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.filterButton, filters.processed === false && styles.filterButtonActive]}
          onPress={() => setFilters(prev => ({ ...prev, processed: false }))}
        >
          <Text style={[styles.filterButtonText, filters.processed === false && styles.filterButtonTextActive]}>
            Unprocessed
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.filterButton, filters.processed === true && styles.filterButtonActive]}
          onPress={() => setFilters(prev => ({ ...prev, processed: true }))}
        >
          <Text style={[styles.filterButtonText, filters.processed === true && styles.filterButtonTextActive]}>
            Processed
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Accounting Data View</Text>
        <TouchableOpacity onPress={onRefresh} style={styles.refreshButton}>
          <Ionicons name="refresh" size={24} color="#007AFF" />
        </TouchableOpacity>
      </View>

      {/* Filters */}
      {renderFilters()}

      {/* Tabs */}
      <View style={styles.tabContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {tabs.map(tab => (
            <TouchableOpacity
              key={tab.key}
              style={[
                styles.tab,
                activeTab === tab.key && { backgroundColor: tab.color }
              ]}
              onPress={() => setActiveTab(tab.key)}
            >
              <Ionicons
                name={tab.icon}
                size={20}
                color={activeTab === tab.key ? 'white' : tab.color}
              />
              <Text
                style={[
                  styles.tabText,
                  activeTab === tab.key && styles.tabTextActive
                ]}
              >
                {tab.title}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Data List */}
      <View style={styles.content}>
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#007AFF" />
            <Text style={styles.loadingText}>Loading {activeTab} data...</Text>
          </View>
        ) : activeTab === 'overview' ? (
          <ScrollView style={styles.overviewContainer}>
            {/* Data Summary Cards */}
            {renderSummaryCard()}
            
            {/* Financial Overview */}
            {overview && (
              <View style={styles.overviewCard}>
                <Text style={styles.overviewTitle}>Financial Overview</Text>
                {renderOverviewItem({ item: overview })}
              </View>
            )}
          </ScrollView>
        ) : activeTab === 'balances' ? (
          <View style={styles.tableContainer}>
            {/* Table Header */}
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, styles.driverNameColumn]}>Driver Name</Text>
              <Text style={[styles.tableHeaderCell, styles.balanceColumn]}>Balance</Text>
              <Text style={[styles.tableHeaderCell, styles.incomeColumn]}>Income</Text>
              <Text style={[styles.tableHeaderCell, styles.rentColumn]}>Company Share</Text>
              <Text style={[styles.tableHeaderCell, styles.finesColumn]}>Fines</Text>
              <Text style={[styles.tableHeaderCell, styles.totalOwedColumn]}>Total Owed</Text>
            </View>
            
            {/* Table Body */}
            <FlatList
              data={getCurrentData()}
              renderItem={renderItem}
              keyExtractor={(item) => item.id || item.driver_id}
              refreshControl={
                <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
              }
              ListEmptyComponent={() => (
                <View style={styles.emptyContainer}>
                  <Ionicons name="document-outline" size={64} color="#ccc" />
                  <Text style={styles.emptyText}>No {activeTab} data found</Text>
                  <Text style={styles.emptySubtext}>
                    Import some data using the fixed import screens
                  </Text>
                </View>
              )}
              showsVerticalScrollIndicator={false}
            />
          </View>
        ) : activeTab === 'careem' ? (
          <View style={styles.tableContainer}>
            {/* Careem Table Header */}
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, styles.driverNameColumn]}>Driver Name</Text>
              <Text style={[styles.tableHeaderCell, styles.captainIdColumn]}>Captain ID</Text>
              <Text style={[styles.tableHeaderCell, styles.phoneColumn]}>Phone</Text>
              <Text style={[styles.tableHeaderCell, styles.earningsColumn]}>Earnings</Text>
              <Text style={[styles.tableHeaderCell, styles.cashBalanceColumn]}>Cash Balance</Text>
              <Text style={[styles.tableHeaderCell, styles.statusColumn]}>Status</Text>
            </View>
            
            <FlatList
              data={getCurrentData()}
              renderItem={renderItem}
              keyExtractor={(item) => item.id}
              refreshControl={
                <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
              }
              ListEmptyComponent={() => (
                <View style={styles.emptyContainer}>
                  <Ionicons name="document-outline" size={64} color="#ccc" />
                  <Text style={styles.emptyText}>No {activeTab} data found</Text>
                  <Text style={styles.emptySubtext}>
                    Import some data using the fixed import screens
                  </Text>
                </View>
              )}
              showsVerticalScrollIndicator={false}
            />
          </View>
        ) : activeTab === 'uber' ? (
          <View style={styles.tableContainer}>
            {/* Uber Table Header */}
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, styles.driverNameColumn]}>Driver Name</Text>
              <Text style={[styles.tableHeaderCell, styles.uberUuidColumn]}>UUID</Text>
              <Text style={[styles.tableHeaderCell, styles.earningsColumn]}>Total Income</Text>
              <Text style={[styles.tableHeaderCell, styles.netFareColumn]}>Net Fare</Text>
              <Text style={[styles.tableHeaderCell, styles.statusColumn]}>Status</Text>
            </View>
            
            <FlatList
              data={getCurrentData()}
              renderItem={renderItem}
              keyExtractor={(item) => item.id}
              refreshControl={
                <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
              }
              ListEmptyComponent={() => (
                <View style={styles.emptyContainer}>
                  <Ionicons name="document-outline" size={64} color="#ccc" />
                  <Text style={styles.emptyText}>No {activeTab} data found</Text>
                  <Text style={styles.emptySubtext}>
                    Import some data using the fixed import screens
                  </Text>
                </View>
              )}
              showsVerticalScrollIndicator={false}
            />
          </View>
        ) : activeTab === 'salik' ? (
          <View style={styles.tableContainer}>
            {/* Salik Table Header */}
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, styles.licensePlateColumn]}>License Plate</Text>
              <Text style={[styles.tableHeaderCell, styles.dateColumn]}>Date</Text>
              <Text style={[styles.tableHeaderCell, styles.tollGateColumn]}>Toll Gate</Text>
              <Text style={[styles.tableHeaderCell, styles.amountColumn]}>Amount</Text>
              <Text style={[styles.tableHeaderCell, styles.driverNameColumn]}>Driver</Text>
              <Text style={[styles.tableHeaderCell, styles.statusColumn]}>Status</Text>
            </View>
            
            <FlatList
              data={getCurrentData()}
              renderItem={renderItem}
              keyExtractor={(item) => item.id}
              refreshControl={
                <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
              }
              ListEmptyComponent={() => (
                <View style={styles.emptyContainer}>
                  <Ionicons name="document-outline" size={64} color="#ccc" />
                  <Text style={styles.emptyText}>No {activeTab} data found</Text>
                  <Text style={styles.emptySubtext}>
                    Import some data using the fixed import screens
                  </Text>
                </View>
              )}
              showsVerticalScrollIndicator={false}
            />
          </View>
        ) : activeTab === 'fines' ? (
          <View style={styles.tableContainer}>
            {/* Fines Table Header */}
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, styles.fineNumberColumn]}>Fine Number</Text>
              <Text style={[styles.tableHeaderCell, styles.dateColumn]}>Issue Date</Text>
              <Text style={[styles.tableHeaderCell, styles.licensePlateColumn]}>License Plate</Text>
              <Text style={[styles.tableHeaderCell, styles.totalAmountColumn]}>Total Amount</Text>
              <Text style={[styles.tableHeaderCell, styles.discountAmountColumn]}>After Discount</Text>
              <Text style={[styles.tableHeaderCell, styles.driverNameColumn]}>Driver</Text>
              <Text style={[styles.tableHeaderCell, styles.statusColumn]}>Status</Text>
            </View>
            
            <FlatList
              data={getCurrentData()}
              renderItem={renderItem}
              keyExtractor={(item) => item.id}
              refreshControl={
                <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
              }
              ListEmptyComponent={() => (
                <View style={styles.emptyContainer}>
                  <Ionicons name="document-outline" size={64} color="#ccc" />
                  <Text style={styles.emptyText}>No {activeTab} data found</Text>
                  <Text style={styles.emptySubtext}>
                    Import some data using the fixed import screens
                  </Text>
                </View>
              )}
              showsVerticalScrollIndicator={false}
            />
          </View>
        ) : (
          <FlatList
            data={getCurrentData()}
            renderItem={renderItem}
            keyExtractor={(item) => item.id}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
            }
            ListEmptyComponent={() => (
              <View style={styles.emptyContainer}>
                <Ionicons name="document-outline" size={64} color="#ccc" />
                <Text style={styles.emptyText}>No {activeTab} data found</Text>
                <Text style={styles.emptySubtext}>
                  Import some data using the fixed import screens
                </Text>
              </View>
            )}
            showsVerticalScrollIndicator={false}
          />
        )}
      </View>
      
      {/* Driver Details Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Driver Balance Details</Text>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={() => setModalVisible(false)}
              >
                <Ionicons name="close" size={24} color="#333" />
              </TouchableOpacity>
            </View>
            
            {selectedDriver && (
              <ScrollView style={styles.modalBody}>
                {/* Driver Name and Balance */}
                <View style={styles.modalBalanceSection}>
                  <Text style={styles.modalDriverName}>
                    {selectedDriver.driver?.name || selectedDriver.driver_name || `Driver ${selectedDriver.driverId || selectedDriver.driver_id}`}
                  </Text>
                  <Text style={[
                    styles.modalBalance,
                    { color: (() => {
                      const incomeTotal = selectedDriver.detailedBreakdown?.income?.total || selectedDriver.totalEarned || 0;
                      const calculatedBalance = incomeTotal - 
                        ((selectedDriver.detailedBreakdown?.rent?.total || 0) +
                         (selectedDriver.detailedBreakdown?.salik?.total || 0) +
                         (selectedDriver.detailedBreakdown?.fines?.total || 0) +
                         (selectedDriver.detailedBreakdown?.other?.total || 0));
                      const balance = calculatedBalance !== 0 ? calculatedBalance : (selectedDriver.currentBalance || selectedDriver.current_balance || 0);
                      return balance >= 0 ? '#10B981' : '#EF4444';
                    })() }
                  ]}>
                    {formatCurrency((() => {
                      const calculatedBalance = (selectedDriver.detailedBreakdown?.income?.total || 0) - 
                        ((selectedDriver.detailedBreakdown?.rent?.total || 0) +
                         (selectedDriver.detailedBreakdown?.salik?.total || 0) +
                         (selectedDriver.detailedBreakdown?.fines?.total || 0) +
                         (selectedDriver.detailedBreakdown?.other?.total || 0));
                      return calculatedBalance !== 0 ? calculatedBalance : (selectedDriver.currentBalance || selectedDriver.current_balance || 0);
                    })())}
                  </Text>
                  <Text style={styles.modalBalanceLabel}>
                    {(() => {
                      const calculatedBalance = (selectedDriver.detailedBreakdown?.income?.total || 0) - 
                        ((selectedDriver.detailedBreakdown?.rent?.total || 0) +
                         (selectedDriver.detailedBreakdown?.salik?.total || 0) +
                         (selectedDriver.detailedBreakdown?.fines?.total || 0) +
                         (selectedDriver.detailedBreakdown?.other?.total || 0));
                      const balance = calculatedBalance !== 0 ? calculatedBalance : (selectedDriver.currentBalance || selectedDriver.current_balance || 0);
                      return balance >= 0 ? 'Amount Due to Driver' : 'Amount Driver Owes';
                    })()}
                  </Text>
                  
                  {/* Payment Button */}
                  <TouchableOpacity 
                    style={[
                      styles.paymentButton,
                      { 
                        backgroundColor: (() => {
                          const calculatedBalance = (selectedDriver.detailedBreakdown?.income?.total || 0) - 
                            ((selectedDriver.detailedBreakdown?.rent?.total || 0) +
                             (selectedDriver.detailedBreakdown?.salik?.total || 0) +
                             (selectedDriver.detailedBreakdown?.fines?.total || 0) +
                             (selectedDriver.detailedBreakdown?.other?.total || 0));
                          const balance = calculatedBalance !== 0 ? calculatedBalance : (selectedDriver.currentBalance || selectedDriver.current_balance || 0);
                          return balance >= 0 ? '#10B981' : '#EF4444';
                        })(),
                        marginTop: 15
                      }
                    ]}
                    onPress={() => handlePayment(selectedDriver)}
                  >
                    <Ionicons 
                      name={(() => {
                        const calculatedBalance = (selectedDriver.detailedBreakdown?.income?.total || 0) - 
                          ((selectedDriver.detailedBreakdown?.rent?.total || 0) +
                           (selectedDriver.detailedBreakdown?.salik?.total || 0) +
                           (selectedDriver.detailedBreakdown?.fines?.total || 0) +
                           (selectedDriver.detailedBreakdown?.other?.total || 0));
                        const balance = calculatedBalance !== 0 ? calculatedBalance : (selectedDriver.currentBalance || selectedDriver.current_balance || 0);
                        return balance >= 0 ? 'cash-outline' : 'card-outline';
                      })()} 
                      size={20} 
                      color="white" 
                    />
                    <Text style={styles.paymentButtonText}>
                      {(() => {
                        const calculatedBalance = (selectedDriver.detailedBreakdown?.income?.total || 0) - 
                          ((selectedDriver.detailedBreakdown?.rent?.total || 0) +
                           (selectedDriver.detailedBreakdown?.salik?.total || 0) +
                           (selectedDriver.detailedBreakdown?.fines?.total || 0) +
                           (selectedDriver.detailedBreakdown?.other?.total || 0));
                        const balance = calculatedBalance !== 0 ? calculatedBalance : (selectedDriver.currentBalance || selectedDriver.current_balance || 0);
                        return balance >= 0 ? 'Pay Driver' : 'Collect Payment';
                      })()}
                    </Text>
                  </TouchableOpacity>
                </View>

                {loadingBreakdown && (
                  <View style={styles.loadingSection}>
                    <ActivityIndicator size="small" color="#007AFF" />
                    <Text style={styles.loadingText}>Loading detailed breakdown...</Text>
                  </View>
                )}

                {/* Income Breakdown */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalSectionTitle}>Income Breakdown</Text>
                  {selectedDriver.detailedBreakdown?.income ? (
                    <View style={styles.modalBreakdownCard}>
                      <TouchableOpacity
                        style={styles.expandableBreakdownHeader}
                        onPress={() => setShowIncomeBreakdown(!showIncomeBreakdown)}
                      >
                        <Text style={styles.modalBreakdownLabel}>Total Income:</Text>
                        <View style={styles.expandableBreakdownRight}>
                          <Text style={styles.modalBreakdownValue}>
                            {formatCurrency(selectedDriver.detailedBreakdown.income.total)}
                          </Text>
                          <Ionicons 
                            name={showIncomeBreakdown ? "chevron-down" : "chevron-forward"} 
                            size={16} 
                            color="#666" 
                          />
                        </View>
                      </TouchableOpacity>
                      
                      {showIncomeBreakdown && (
                        <View style={styles.expandedBreakdownContent}>
                          {selectedDriver.detailedBreakdown.income.details.map((item, index) => (
                            <View key={`income-${index}-${item.source}-${item.amount}`} style={styles.detailedBreakdownItem}>
                              <Text style={styles.detailedItemDescription}>{item.description}</Text>
                              <Text style={styles.detailedItemAmount}>{formatCurrency(item.amount)}</Text>
                            </View>
                          ))}
                        </View>
                      )}
                    </View>
                  ) : (
                    <View style={styles.modalBreakdownCard}>
                      <View style={styles.modalBreakdownRow}>
                        <Text style={styles.modalBreakdownLabel}>Careem Income:</Text>
                        <Text style={styles.modalBreakdownValue}>
                          {formatCurrency(selectedDriver.totalCareemIncome || 0)}
                        </Text>
                      </View>
                      <View style={styles.modalBreakdownRow}>
                        <Text style={styles.modalBreakdownLabel}>Uber Income:</Text>
                        <Text style={styles.modalBreakdownValue}>
                          {formatCurrency(selectedDriver.totalUberIncome || 0)}
                        </Text>
                      </View>
                      <View style={[styles.modalBreakdownRow, styles.modalBreakdownTotal]}>
                        <Text style={styles.modalBreakdownTotalLabel}>Total Income:</Text>
                        <Text style={[styles.modalBreakdownValue, styles.totalEarned]}>
                          {formatCurrency(selectedDriver.totalEarned || 0)}
                        </Text>
                      </View>
                    </View>
                  )}
                </View>

                {/* Deductions Breakdown */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalSectionTitle}>Deductions Breakdown</Text>
                  
                  {/* Rent Details with Detailed Breakdown */}
                  <View style={styles.modalBreakdownCard}>
                    <TouchableOpacity
                      style={styles.expandableBreakdownHeader}
                      onPress={() => setShowRentBreakdown(!showRentBreakdown)}
                    >
                      <Text style={styles.modalBreakdownLabel}>Company Share:</Text>
                      <View style={styles.expandableBreakdownRight}>
                        <Text style={styles.modalBreakdownValue}>
                          {formatCurrency(selectedDriver.detailedBreakdown?.rent?.total || selectedDriver.totalRentCharged || 0)}
                        </Text>
                        <Ionicons 
                          name={showRentBreakdown ? "chevron-down" : "chevron-forward"} 
                          size={16} 
                          color="#666" 
                        />
                      </View>
                    </TouchableOpacity>
                    
                    {showRentBreakdown && selectedDriver.detailedBreakdown?.rent && (
                      <View style={styles.expandedBreakdownContent}>
                        {selectedDriver.detailedBreakdown.rent.details.length > 0 ? (
                          selectedDriver.detailedBreakdown.rent.details.map((item, index) => (
                            <View key={`rent-${index}-${item.amount}-${item.period}`} style={styles.detailedBreakdownItem}>
                              <View style={styles.detailedItemContent}>
                                <Text style={styles.detailedItemDescription}>{item.description}</Text>
                                <Text style={styles.detailedItemSubtext}>Vehicle: {item.vehicle}</Text>
                              </View>
                              <Text style={styles.detailedItemAmount}>{formatCurrency(item.amount)}</Text>
                            </View>
                          ))
                        ) : (
                          <Text style={styles.noDataText}>No assignment records found</Text>
                        )}
                      </View>
                    )}
                  </View>

                  {/* Salik Details with Detailed Breakdown */}
                  <View style={styles.modalBreakdownCard}>
                    <TouchableOpacity
                      style={styles.expandableBreakdownHeader}
                      onPress={() => setShowSalikBreakdown(!showSalikBreakdown)}
                    >
                      <Text style={styles.modalBreakdownLabel}>Salik Tolls:</Text>
                      <View style={styles.expandableBreakdownRight}>
                        <Text style={styles.modalBreakdownValue}>
                          {formatCurrency(selectedDriver.detailedBreakdown?.salik?.total || selectedDriver.totalSalik || 0)}
                        </Text>
                        <Ionicons 
                          name={showSalikBreakdown ? "chevron-down" : "chevron-forward"} 
                          size={16} 
                          color="#666" 
                        />
                      </View>
                    </TouchableOpacity>
                    
                    {showSalikBreakdown && selectedDriver.detailedBreakdown?.salik && (
                      <View style={styles.expandedBreakdownContent}>
                        {selectedDriver.detailedBreakdown.salik.details.length > 0 ? (
                          selectedDriver.detailedBreakdown.salik.details.map((item, index) => (
                            <View key={`salik-${index}-${item.amount}-${item.count}`} style={styles.detailedBreakdownItem}>
                              <Text style={styles.detailedItemDescription}>{item.description}</Text>
                              <Text style={styles.detailedItemAmount}>{formatCurrency(item.amount)}</Text>
                            </View>
                          ))
                        ) : (
                          <Text style={styles.noDataText}>No Salik transactions found</Text>
                        )}
                      </View>
                    )}
                  </View>

                  {/* Fines Details with Detailed Breakdown */}
                  <View style={styles.modalBreakdownCard}>
                    <TouchableOpacity
                      style={styles.expandableBreakdownHeader}
                      onPress={() => setShowFinesBreakdown(!showFinesBreakdown)}
                    >
                      <Text style={styles.modalBreakdownLabel}>Traffic Fines:</Text>
                      <View style={styles.expandableBreakdownRight}>
                        <Text style={styles.modalBreakdownValue}>
                          {formatCurrency(selectedDriver.detailedBreakdown?.fines?.total || selectedDriver.totalFines || 0)}
                        </Text>
                        <Ionicons 
                          name={showFinesBreakdown ? "chevron-down" : "chevron-forward"} 
                          size={16} 
                          color="#666" 
                        />
                      </View>
                    </TouchableOpacity>
                    
                    {showFinesBreakdown && selectedDriver.detailedBreakdown?.fines && (
                      <View style={styles.expandedBreakdownContent}>
                        {selectedDriver.detailedBreakdown.fines.details.length > 0 ? (
                          selectedDriver.detailedBreakdown.fines.details.map((item, index) => (
                            <View key={`fine-${index}-${item.amount}-${item.type}`} style={styles.detailedBreakdownItem}>
                              <Text style={styles.detailedItemDescription}>{item.description}</Text>
                              <Text style={styles.detailedItemAmount}>{formatCurrency(item.amount)}</Text>
                            </View>
                          ))
                        ) : (
                          <Text style={styles.noDataText}>No traffic fines found</Text>
                        )}
                      </View>
                    )}
                  </View>
                </View>

                {/* Summary */}
                <View style={styles.modalSection}>
                  <Text style={styles.modalSectionTitle}>Summary</Text>
                  <View style={styles.modalBreakdownCard}>
                    <View style={styles.modalBreakdownRow}>
                      <Text style={styles.modalBreakdownLabel}>Total Earned:</Text>
                      <Text style={[styles.modalBreakdownValue, styles.totalEarned]}>
                        {formatCurrency(selectedDriver.detailedBreakdown?.income?.total || selectedDriver.totalEarned || 0)}
                      </Text>
                    </View>
                    <View style={[styles.modalBreakdownRow, styles.modalBreakdownTotal]}>
                      <Text style={styles.modalBreakdownTotalLabel}>Total Amount Owed:</Text>
                      <Text style={[styles.modalBreakdownValue, styles.modalBreakdownTotalValue]}>
                        {formatCurrency(
                          (selectedDriver.detailedBreakdown?.rent?.total || 0) +
                          (selectedDriver.detailedBreakdown?.salik?.total || 0) +
                          (selectedDriver.detailedBreakdown?.fines?.total || 0) +
                          (selectedDriver.detailedBreakdown?.other?.total || 0) ||
                          selectedDriver.totalOwed || 0
                        )}
                      </Text>
                    </View>
                  </View>
                </View>
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: 'white',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  refreshButton: {
    padding: 5,
  },
  summaryContainer: {
    backgroundColor: 'white',
    margin: 15,
    padding: 15,
    borderRadius: 10,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  summaryTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  summaryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  summaryCard: {
    width: '48%',
    backgroundColor: '#f9f9f9',
    padding: 12,
    borderRadius: 8,
    marginBottom: 10,
    borderLeftWidth: 4,
  },
  summaryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  summaryLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
    color: '#333',
  },
  summaryCount: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  summaryProcessed: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  summaryAmount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#007AFF',
    marginTop: 4,
  },
  filtersContainer: {
    backgroundColor: 'white',
    marginHorizontal: 15,
    marginBottom: 15,
    padding: 15,
    borderRadius: 10,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  filtersTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
  },
  filtersRow: {
    flexDirection: 'row',
    marginBottom: 10,
  },
  filterInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 5,
    padding: 10,
    fontSize: 14,
  },
  filterButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 5,
    marginRight: 10,
    alignItems: 'center',
  },
  filterButtonActive: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  filterButtonText: {
    fontSize: 14,
    color: '#666',
  },
  filterButtonTextActive: {
    color: 'white',
    fontWeight: '600',
  },
  tabContainer: {
    backgroundColor: 'white',
    marginHorizontal: 15,
    marginBottom: 15,
    borderRadius: 10,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    marginHorizontal: 5,
    marginVertical: 5,
    borderRadius: 8,
    backgroundColor: '#f0f0f0',
  },
  tabText: {
    marginLeft: 8,
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  tabTextActive: {
    color: 'white',
  },
  content: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#666',
  },
  dataCard: {
    backgroundColor: 'white',
    marginHorizontal: 15,
    marginBottom: 10,
    padding: 15,
    borderRadius: 10,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  dataHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  dataTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginLeft: 10,
  },
  statusText: {
    fontSize: 12,
    color: 'white',
    fontWeight: '600',
  },
  dataRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 5,
  },
  dataLabel: {
    fontSize: 14,
    color: '#666',
    flex: 1,
  },
  dataValue: {
    fontSize: 14,
    color: '#333',
    flex: 2,
    textAlign: 'right',
  },
  amountText: {
    fontWeight: '600',
    color: '#007AFF',
  },
  dataTimestamp: {
    fontSize: 12,
    color: '#999',
    marginTop: 10,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 50,
  },
  emptyText: {
    fontSize: 18,
    color: '#666',
    marginTop: 10,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 5,
    textAlign: 'center',
  },
  // Overview specific styles
  overviewContainer: {
    flex: 1,
    padding: 15,
  },
  overviewCard: {
    backgroundColor: 'white',
    borderRadius: 10,
    padding: 20,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  overviewTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 20,
    textAlign: 'center',
  },
  summaryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 25,
  },
  summaryItem: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 15,
    width: '48%',
    marginBottom: 15,
    alignItems: 'center',
    borderLeftWidth: 4,
  },
  summaryItemIncome: {
    borderLeftColor: '#10B981',
  },
  summaryItemExpense: {
    borderLeftColor: '#EF4444',
  },
  summaryItemNet: {
    borderLeftColor: '#3B82F6',
  },
  summaryItemBalance: {
    borderLeftColor: '#8B5CF6',
  },
  summaryValue: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  summaryValueIncome: {
    color: '#10B981',
  },
  summaryValueExpense: {
    color: '#EF4444',
  },
  summaryValueNet: {
    color: '#3B82F6',
  },
  summaryValueBalance: {
    color: '#8B5CF6',
  },
  summaryLabel: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
  },
  categorySummary: {
    marginBottom: 20,
  },
  categoryTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 10,
  },
  categoryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#f8f9fa',
    borderRadius: 6,
    marginBottom: 5,
  },
  categoryLabel: {
    fontSize: 14,
    color: '#333',
    flex: 1,
  },
  categoryAmount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  // Balance specific styles
  balanceCard: {
    backgroundColor: 'white',
    borderRadius: 10,
    padding: 15,
    marginHorizontal: 15,
    marginVertical: 8,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  balanceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  balanceDriverName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    flex: 1,
  },
  balanceAmount: {
    fontSize: 18,
    fontWeight: 'bold',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    color: 'white',
  },
  balancePositive: {
    backgroundColor: '#10B981',
  },
  balanceNegative: {
    backgroundColor: '#EF4444',
  },
  balanceZero: {
    backgroundColor: '#6B7280',
  },
  balanceDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  balanceDetailItem: {
    alignItems: 'center',
    flex: 1,
  },
  balanceDetailLabel: {
    fontSize: 12,
    color: '#666',
    marginBottom: 2,
  },
  balanceDetailValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  
  // Table Styles for Balance View
  tableContainer: {
    flex: 1,
    backgroundColor: '#fff',
    margin: 10,
    borderRadius: 8,
    overflow: 'hidden',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  tableHeaderCell: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    textAlign: 'center',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  tableRowEven: {
    backgroundColor: '#ffffff',
  },
  tableRowOdd: {
    backgroundColor: '#f9fafb',
  },
  tableCell: {
    fontSize: 11,
    color: '#374151',
    textAlign: 'center',
  },
  driverNameColumn: {
    flex: 2.5,
    textAlign: 'left',
  },
  balanceColumn: {
    flex: 1.5,
    fontWeight: '600',
  },
  incomeColumn: {
    flex: 1.3,
  },
  rentColumn: {
    flex: 1.3,
  },
  finesColumn: {
    flex: 1.2,
  },
  totalOwedColumn: {
    flex: 1.5,
  },
  // Careem specific columns
  captainIdColumn: {
    flex: 1.2,
  },
  phoneColumn: {
    flex: 1.3,
  },
  earningsColumn: {
    flex: 1.2,
  },
  cashBalanceColumn: {
    flex: 1.2,
  },
  // Uber specific columns
  uberUuidColumn: {
    flex: 1,
  },
  netFareColumn: {
    flex: 1.2,
  },
  // Salik specific columns
  licensePlateColumn: {
    flex: 1,
  },
  dateColumn: {
    flex: 1.2,
  },
  tollGateColumn: {
    flex: 1.5,
  },
  amountColumn: {
    flex: 1,
  },
  // Fines specific columns
  fineNumberColumn: {
    flex: 1.2,
  },
  totalAmountColumn: {
    flex: 1.2,
  },
  discountAmountColumn: {
    flex: 1.3,
  },
  // Mini status badge for tables
  miniStatusBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
    alignSelf: 'center',
  },
  miniStatusText: {
    color: 'white',
    fontSize: 10,
    fontWeight: '600',
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 15,
    padding: 0,
    width: '90%',
    maxHeight: '80%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e1e1',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#333',
  },
  closeButton: {
    padding: 5,
  },
  modalBody: {
    padding: 20,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  detailLabel: {
    fontSize: 16,
    color: '#666',
    flex: 1,
  },
  detailValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    flex: 1,
    textAlign: 'right',
  },
  balanceAmount: {
    fontSize: 18,
    fontWeight: '700',
  },
  incomeAmount: {
    color: '#10B981',
  },
  rentAmount: {
    color: '#F59E0B',
  },
  finesAmount: {
    color: '#EF4444',
  },
  totalRow: {
    marginTop: 10,
    paddingTop: 15,
    paddingVertical: 15,
    borderTopWidth: 2,
    borderTopColor: '#e1e1e1',
    borderBottomWidth: 0,
    backgroundColor: '#f8f9fa',
    marginHorizontal: -20,
    paddingHorizontal: 20,
  },
  totalLabel: {
    fontSize: 18,
    fontWeight: '700',
    color: '#333',
    flex: 1,
  },
  totalValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#333',
    flex: 1,
    textAlign: 'right',
  },
  
  // Enhanced Modal Styles
  modalBalanceSection: {
    alignItems: 'center',
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    marginBottom: 20,
  },
  modalDriverName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  modalBalance: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },
  modalBalanceLabel: {
    fontSize: 14,
    color: '#666',
  },
  modalSection: {
    marginBottom: 20,
  },
  modalSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  modalBreakdownCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  modalBreakdownRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  modalBreakdownLabel: {
    fontSize: 14,
    color: '#666',
    flex: 1,
  },
  modalBreakdownValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  modalBreakdownTotal: {
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    marginTop: 8,
    paddingTop: 12,
  },
  modalBreakdownTotalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  modalBreakdownTotalValue: {
    fontSize: 16,
    fontWeight: '700',
    color: '#333',
  },
  expandableBreakdownHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
  },
  expandableBreakdownRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  expandedBreakdownContent: {
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    paddingTop: 12,
  },
  detailedBreakdownItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  detailedItemContent: {
    flex: 1,
    marginRight: 12,
  },
  detailedItemDescription: {
    fontSize: 13,
    color: '#333',
    fontWeight: '500',
    marginBottom: 2,
  },
  detailedItemSubtext: {
    fontSize: 12,
    color: '#666',
  },
  detailedItemAmount: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
  },
  noDataText: {
    fontSize: 13,
    color: '#666',
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 8,
  },
  loadingSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    gap: 8,
  },
  loadingText: {
    fontSize: 14,
    color: '#666',
  },
  totalEarned: {
    color: '#10B981',
    fontWeight: '600',
  },
  paymentButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    gap: 8,
  },
  paymentButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default AccountingViewScreen;
